import React from "react";
import validate from "validate.js";
import { StateHandle } from "StateHandle";
import { Cell, CellType, CellValidationConfig, Form, TextCellValidationStrategy } from "generated/graphql";
import { isFormValueAnswered } from "helpers/isFormValueAnswered";
import { FieldCellValueUI, FormFieldValuesUI, cellValueFromGQL } from "Backend";
import { stripNull, stripNullEmpty } from "./coll";

validate.validators.password = (value: string) => {
  if (!/[ !"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/.test(value)) {
    return "^At least one symbol is required";
  } else if (!/[0-9]/.test(value)) {
    return "^At least one digit is required";
  } else if (!/[a-z]/.test(value)) {
    return "^At least one lower-case letter is required";
  } else if (!/[A-Z]/.test(value)) {
    return "^At least one upper-case letter is required";
  }

  return null;
};

export interface FormState<Field extends string> {
  isValid: boolean;
  values: Record<Field, any>;
  touched: Record<Field, boolean>;
  errors: Partial<Record<Field, string[]>>;
}

function handleChange<Field extends string>(setFormState: (state: React.SetStateAction<FormState<Field>>) => void) {
  const fn = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.persist();
    setFormState((formState) => ({
      ...formState,
      values: {
        ...formState.values,
        [event.target.name]: event.target.type === "checkbox" ? event.target.checked : event.target.value,
      },
      touched: {
        ...formState.touched,
        [event.target.name]: true,
      },
    }));
  };
  return fn;
}

function updateField<Field extends string>(
  setFormState: (state: React.SetStateAction<FormState<Field>>) => void
): (field: Field) => (value: any) => void {
  return (field: Field) => (value: any) => {
    setFormState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        [field]: value,
      },
      touched: {
        ...prev.touched,
        [field]: true,
      },
    }));
  };
}

export interface UseForm<Field extends string> {
  formStateHandle: StateHandle<FormState<Field>>;
  hasError: (field: Field) => boolean;
  isGood: (field: Field) => boolean;
  errorText: (field: Field) => string | undefined;
  handleChange: React.ChangeEventHandler<HTMLInputElement>;
  updateField: (field: Field) => (value: any) => void;
  setFieldError: (field: Field) => (errors: string[] | undefined) => void;
}

interface DateSchema<Field extends string> {
  name: Field;
}

export function useForm<Field extends string>(
  initial: FormState<Field>,
  schema: Record<Field, object>,
  dates?: DateSchema<Field>[]
): UseForm<Field> {
  const [formState, setFormState] = React.useState<FormState<Field>>(initial);

  React.useEffect(() => {
    const dateErrors = Object.fromEntries(
      Object.entries(formState.errors).filter(([field, _error]) => dates?.find((d) => d.name === field))
    );
    const errors = { ...validate(formState.values, schema), ...dateErrors };
    const isValid = Object.entries(errors).every(([_key, v]) => !v);
    setFormState((formState) => ({
      ...formState,
      isValid,
      errors: errors,
    }));
  }, [formState.values]);

  const hasError = (field: Field) => {
    if (formState.touched[field]) {
      const errorArray = formState.errors[field];
      return (errorArray?.length || 0) > 0;
    } else return false;
  };

  const setFieldError = (field: Field) => {
    return (error: string[] | undefined) => {
      setFormState((prev) => ({
        ...prev,
        errors: {
          ...prev.errors,
          [field]: error,
        },
      }));
    };
  };

  return {
    formStateHandle: [formState, setFormState],
    hasError: hasError,
    isGood: (field: Field) => !hasError(field) && formState.touched[field],
    errorText: (field: Field) => (hasError(field) ? formState.errors[field]?.[0] : undefined),
    handleChange: handleChange<Field>(setFormState),
    updateField: updateField<Field>(setFormState),
    setFieldError,
  };
}

export interface CheckboxListItem {
  value: string;
  name: string;
}

const noWhitespace = "^(\\S*)$";
const alphanumericLowerCase = "^[a-z0-9_]*";

export const schemas = {
  email: {
    presence: { allowEmpty: false },
    email: true,
    length: {
      maximum: 64,
    },
  },
  password: {
    presence: { allowEmpty: false },
    length: {
      maximum: 128,
      minimum: 8,
    },
    password: {},
  },
  systemName: (name?: string, cells?: Array<Cell>) => {
    const systemNames = cells?.flatMap((cell) => {
      if (cell.systemName) return [cell.systemName];
      else return [];
    });
    return {
      exclusion: {
        within: systemNames,
        message: `^${name} %{value} is already taken by field`,
      },
      presence: {
        allowEmpty: false,
        message: name ? () => `^${name} is required` : null,
      },
      length: {
        maximum: 63,
      },
      format: {
        pattern: alphanumericLowerCase,
        message: (_value: any, attribute: any) =>
          `^${name || attribute} must only contain lowercase letters, numbers, or underscores`,
      },
    };
  },
  nonEmpty: (name?: string, max?: number) => ({
    presence: {
      allowEmpty: false,
      message: name ? () => `^${name} is required` : null,
    },
    length: {
      maximum: max || 256,
    },
  }),
  nonEmptyArray: {
    length: {
      minimum: 1,
    },
    presence: {
      allowEmpty: false,
    },
  },
  optionalArray: {
    presence: {
      allowEmpty: true,
    },
  },
  customType: (required: boolean) => ({
    presence: required,
  }),
  basicOptional: (n?: number) => ({
    length: {
      maximum: n || 128,
    },
  }),
  ident: (name?: string) => ({
    format: {
      pattern: noWhitespace,
      message: (_value: any, attribute: any) => `^${name || attribute} cannot contain spaces`,
    },
    presence: {
      allowEmpty: false,
      message: (_value: any, attribute: any) => `^${name || attribute} is required`,
    },
  }),
};

type ValidationFunction<T> = (value: T, config: CellValidationConfig) => string[];
type ValidationInput<T> = { type: CellType; value: T };

const StrategyMap: { [key in TextCellValidationStrategy]: [RegExp | null, string | null] } = {
  [TextCellValidationStrategy.Ssn]: [/^\d{3}-?\d{2}-?\d{4}$/, "SSN is invalid (XXX-XX-XXXX)"],
  [TextCellValidationStrategy.Custom]: [null, null],
  [TextCellValidationStrategy.Numeric]: [/^(?:\d*\.)?\d+$/, "Must be a number"],
  [TextCellValidationStrategy.None]: [null, null],
};

const validateTextCell: ValidationFunction<string> = (value, config) => {
  const messages: string[] = [];

  if (config === undefined || stripNullEmpty(value) === undefined) return messages;
  const hasMinConfig = config.minValue !== undefined && config.minValue !== null;
  const hasMaxConfig = config.maxValue !== undefined && config.maxValue !== null;

  if (hasMinConfig && value.length < config.minValue!) {
    messages.push(`Value must be at least ${config.minValue} characters long`);
  } else if (hasMaxConfig && value.length > config.maxValue!) {
    messages.push(`Value must be no more than ${config.maxValue} characters long`);
  }

  const [strategyRegex, customError] = StrategyMap[config.strategy || TextCellValidationStrategy.Custom];
  const regex =
    strategyRegex ||
    (config.regex === undefined || config.regex === null ? undefined : new RegExp(`^${config.regex}$`));

  if (regex && !regex.test(value)) {
    messages.push(customError || "Value doesn't match pattern");
  }

  return messages;
};

const validateNumericCell: ValidationFunction<number> = (value, config) => {
  const messages: string[] = [];

  if (config === undefined || stripNull(value) === undefined) return messages;
  const hasMinConfig = config.minValue !== undefined && config.minValue !== null;
  const hasMaxConfig = config.maxValue !== undefined && config.maxValue !== null;

  if (hasMinConfig && value < config.minValue!) {
    messages.push(`Value must be greater than or equal to ${config.minValue}`);
  } else if (hasMaxConfig && value > config.maxValue!) {
    messages.push(`Value must be less than or equal to ${config.maxValue}`);
  }

  return messages;
};

const ValidationMap: { [key in CellType]: ValidationFunction<any> } = {
  [CellType.Float]: validateNumericCell,
  [CellType.Integer]: validateNumericCell,
  [CellType.Text]: validateTextCell,
  [CellType.Boolean]: () => [],
  [CellType.Date]: () => [],
  [CellType.Select]: () => [],
  [CellType.MultiSelect]: () => [],
};

export function getCellValidationErrors(input: ValidationInput<any>, config: CellValidationConfig): string[] {
  const cellType = input.type;

  if (!cellType) return [];

  const validationFn = ValidationMap[cellType];

  return validationFn(input.value, config);
}

export function getCellValidationErrorsForValue(cellValue: FieldCellValueUI, config: CellValidationConfig): string[] {
  const cellType = cellValue.value.type;
  switch (cellType) {
    case CellType.Text:
      return getCellValidationErrors({ value: cellValue.value.textValue, type: cellType }, config);
    case CellType.Float:
      return getCellValidationErrors({ value: cellValue.value.floatValue, type: cellType }, config);
    case CellType.Integer:
      return getCellValidationErrors({ value: cellValue.value.integerValue, type: cellType }, config);
    default:
      return [];
  }
}

export function validateCellValue(cellValue: FieldCellValueUI, config: CellValidationConfig): boolean {
  return getCellValidationErrorsForValue(cellValue, config).length === 0;
}

export function validateCell(value: FieldCellValueUI | undefined, schema: Cell): boolean {
  if (value === undefined) return schema.optional;
  if (value.error) {
    return false;
  } else if (cellValueFromGQL(value.value) === undefined) {
    return schema.optional;
  } else {
    return validateCellValue(value, schema.validationConfig || {});
  }
}

export function checkMissingRequired(form?: Form, formValues?: FormFieldValuesUI[]): string[] {
  if (!form) return [];
  if (!formValues) return [];

  const missing: string[] = [];
  for (const [n, field] of form.fields.entries()) {
    if (field.required) {
      const fieldValue = formValues.find((v) => v.fieldId === field.id);
      const isAnswered = isFormValueAnswered(field, fieldValue);
      if (!isAnswered) {
        missing.push(`${field.outlinePrefix ? field.outlinePrefix : n + 1}`);
      }
    }
  }
  return missing;
}

export function checkComplete(form?: Form, formValues?: FormFieldValuesUI[]): boolean {
  if (!form) return false;
  if (!formValues) return false;

  for (const field of form.fields) {
    const fieldValue = formValues.find((v) => v.fieldId === field.id);
    const isAnswered = isFormValueAnswered(field, fieldValue);
    if (!isAnswered) return false;
  }
  return true;
}
